/**
 * `CHANGE_DAY_MASTER` step. Flips `cse.day_of_the_week` for the moved CSEs;
 * with `futureStops:true` the BE shifts all future WOs to the target day.
 * `skipInvalidate` not passed — the endpoint doesn't yet accept it.
 */
import chunk from 'lodash/chunk'

import { err, ok } from 'types/entities/shared/result'

import { extractBackendError } from '../backendError'

import type { CseId, DayOfWeek, RouteId } from 'types/entities/shared/ids'
import type { ApiDayOfWeek } from 'types/api/route/masterStopRequests'
import type { Deps, PipelineStep } from 'types/modules/routing'

import type { StepResult } from '../types'

const BATCH_SIZE = 200

export interface ChangeDayMasterStepArgs {
  readonly routeId: RouteId
  readonly fromDayOfWeek: DayOfWeek
  readonly toDayOfWeek: DayOfWeek
  readonly cseIds: ReadonlyArray<CseId>
  readonly futureStops?: boolean
}

export type ChangeDayMasterStepValue = {
  readonly cseCount: number
  readonly batchCount: number
}

export type ChangeDayMasterStepResult = StepResult<'CHANGE_DAY_MASTER', ChangeDayMasterStepValue>

export const makeChangeDayMasterStep = (
  args: ChangeDayMasterStepArgs,
  deps: Deps,
): PipelineStep<'CHANGE_DAY_MASTER', ChangeDayMasterStepResult> => {
  const stepId = `change-day-master:${args.routeId}:${args.fromDayOfWeek}->${args.toDayOfWeek}`
  const futureStops = args.futureStops ?? true
  const dayOfWeek = args.toDayOfWeek as ApiDayOfWeek
  return {
    id: stepId,
    phase: 'CHANGE_DAY_MASTER',
    label: 'Failed to change day of week for master stops',
    applyOrder: 100,
    run: async (_ctx) => {
      if (args.cseIds.length === 0) {
        return ok({
          phase: 'CHANGE_DAY_MASTER',
          stepId,
          value: { cseCount: 0, batchCount: 0 },
        })
      }
      const batches = chunk(args.cseIds as readonly string[], BATCH_SIZE)
      try {
        for (const batch of batches) {
          await deps
            .changeDayMaster({
              routeId: args.routeId,
              data: {
                dayOfWeek,
                futureStops,
                stops: [...batch],
              },
            })
            .unwrap()
        }
        return ok({
          phase: 'CHANGE_DAY_MASTER',
          stepId,
          value: { cseCount: args.cseIds.length, batchCount: batches.length },
        })
      } catch (cause: unknown) {
        const { status, message } = extractBackendError(cause)
        return err({
          kind: 'BE_REJECTED',
          phase: 'CHANGE_DAY_MASTER',
          status,
          message,
        })
      }
    },
  }
}
